/**
 * Custom Icons
 *
 * Custom icon components for integrations that are missing from @tabler/icons-react
 *
 * Primary responsibilities:
 * - Provide custom SVG icons for services not available in the Tabler icon set
 * - Maintain consistent styling with other Tabler icons
 * - Support dark mode with appropriate color adjustments
 */

import React, { useEffect, useState } from 'react'
import { useTheme } from 'next-themes'

interface IconProps {
  size?: number
  stroke?: number
  color?: string
  className?: string
}

// Helper hook to safely use theme on client side only
const useClientTheme = () => {
  const { resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  return {
    isDarkMode: mounted && resolvedTheme === 'dark',
    mounted
  }
}

export const IconSave: React.FC<IconProps> = ({
  size = 24,
  stroke = 2,
  color,
  className = '',
  ...props
}) => {
  const { isDarkMode, mounted } = useClientTheme()
  const iconColor = color || (isDarkMode ? '#e2e8f0' : '#0f172a')

  // Return a placeholder with the same dimensions during SSR
  if (!mounted) {
    return <div style={{ width: size, height: size }} />
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={iconColor}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M6 4h10l4 4v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      <path d="M12 14v4" />
      <path d="M10 14h4" />
      <path d="M14 4v4H8V4" />
    </svg>
  )
}

export const IconBrandHubspot: React.FC<IconProps> = ({
  size = 24,
  stroke = 2,
  color,
  className = '',
  ...props
}) => {
  const { isDarkMode, mounted } = useClientTheme()
  const iconColor = color || (isDarkMode ? '#ff7a59' : '#ff7a59') // HubSpot orange in both modes

  if (!mounted) {
    return <div style={{ width: size, height: size }} />
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={iconColor}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0-18 0z" />
      <path d="M7 9h5v5h-5z" />
      <path d="M12 9h5v5h-5z" />
      <path d="M7 14h5v5h-5z" />
      <path d="M12 14h5v5h-5z" />
    </svg>
  )
}

export const IconBrandJira: React.FC<IconProps> = ({
  size = 24,
  stroke = 2,
  color,
  className = '',
  ...props
}) => {
  const { isDarkMode, mounted } = useClientTheme()
  const iconColor = color || (isDarkMode ? '#2684FF' : '#0052CC') // Jira blue, lighter in dark mode

  if (!mounted) {
    return <div style={{ width: size, height: size }} />
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={iconColor}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M11.18 12.36L8.59 15l2.82 2.82L16 13.24V4h-9.18z" />
      <path d="M5.82 10.82L3 13.64l2.82 2.82 5.36-5.36-5.36-5.36z" />
      <path d="M16.54 17.18L19.36 20l2.82-2.82-5.36-5.36-5.36 5.36z" />
    </svg>
  )
}

export const IconBrandMicrosoft: React.FC<IconProps> = ({
  size = 24,
  stroke = 2,
  color,
  className = '',
  ...props
}) => {
  const { isDarkMode, mounted } = useClientTheme()
  const iconColor = color || (isDarkMode ? '#ffffff' : '#000000') // White in dark mode, black in light mode

  if (!mounted) {
    return <div style={{ width: size, height: size }} />
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={iconColor}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M11 5H5v6h6V5z" />
      <path d="M19 5h-6v6h6V5z" />
      <path d="M11 13H5v6h6v-6z" />
      <path d="M19 13h-6v6h6v-6z" />
    </svg>
  )
}

export const IconCloudStorage: React.FC<IconProps> = ({
  size = 24,
  stroke = 2,
  color,
  className = '',
  ...props
}) => {
  const { isDarkMode, mounted } = useClientTheme()
  const iconColor = color || (isDarkMode ? '#e2e8f0' : '#0f172a')

  if (!mounted) {
    return <div style={{ width: size, height: size }} />
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={iconColor}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M19 18a3.5 3.5 0 0 0 0-7h-1A5 5 0 0 0 7 6.5a5 5 0 0 0-2.5 9.5H7" />
      <path d="M9 15l3-3l3 3" />
      <path d="M12 12v9" />
    </svg>
  )
}

export const IconBrandDropbox: React.FC<IconProps> = ({
  size = 24,
  stroke = 2,
  color,
  className = '',
  ...props
}) => {
  const { isDarkMode, mounted } = useClientTheme()
  const iconColor = color || (isDarkMode ? '#0d2481' : '#0061ff') // Dropbox blue, darker in dark mode

  if (!mounted) {
    return <div style={{ width: size, height: size }} />
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={iconColor}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M6 5.5l6 4l6-4" />
      <path d="M6 14.5l6 4l6-4" />
      <path d="M6 9.5l6-4l6 4" />
      <path d="M12 13.5v4" />
    </svg>
  )
}

export const IconBrandCanva: React.FC<IconProps> = ({
  size = 24,
  stroke: _stroke = 2,
  color,
  className = '',
  ...props
}) => {
  const { isDarkMode, mounted } = useClientTheme()
  const iconColor = color || (isDarkMode ? '#7dd3fc' : '#00c4cc')

  if (!mounted) {
    return <div style={{ width: size, height: size }} />
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="9" fill={iconColor} />
      <path
        d="M15.5 8.6a4.5 4.5 0 1 0 0 6.8"
        stroke="#ffffff"
        strokeWidth="2.25"
        strokeLinecap="round"
      />
    </svg>
  )
}
